// apps/admin-ui/src/components/charts/DonutChart.tsx
// CSS-only donut chart using conic-gradient
// Zero JavaScript - pure CSS for instant render
// ALL RIGHTS RESERVED

import '../../styles/charts.css';

export interface DonutChartSegment {
  label: string;
  value: number;    // Percentage (should sum to 100)
  color: string;    // CSS color or variable
}

export interface DonutChartProps {
  segments: DonutChartSegment[];
  size?: number;    // Size in pixels (default: 120)
}

/**
 * Donut Chart
 * 
 * Renders a donut/pie chart using CSS conic-gradient.
 * Zero CPU overhead - browser handles all rendering natively.
 * 
 * Features:
 * - Pure CSS rendering (no canvas, no SVG)
 * - Automatic percentage calculation
 * - Legend with segment labels
 */
export function DonutChart({ segments, size = 120 }: DonutChartProps) {
  // Build conic-gradient stops
  let currentAngle = 0;
  const gradientStops = segments.map((segment) => {
    const startAngle = currentAngle;
    const endAngle = currentAngle + segment.value;
    currentAngle = endAngle;
    return `${segment.color} ${startAngle}% ${endAngle}%`;
  }).join(', ');

  const chartStyle = {
    width: `${size}px`,
    height: `${size}px`,
    background: `conic-gradient(${gradientStops})`,
  };

  return (
    <div className="donut-chart-container">
      <div 
        className="donut-chart"
        style={chartStyle}
        role="img"
        aria-label={`Chart showing ${segments.map(s => `${s.label} ${s.value}%`).join(', ')}`}
      >
        <div className="donut-hole" />
      </div>
      
      <ul className="donut-legend">
        {segments.map((segment, index) => (
          <li key={index} className="donut-legend-item">
            <span 
              className="donut-legend-color" 
              style={{ background: segment.color }}
            />
            <span className="donut-legend-label">{segment.label}</span>
            <span className="donut-legend-value">{segment.value}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default DonutChart;
